import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Typography,
  Switch,
  FormControlLabel,
} from '@mui/material';

import Layout from '../../components/layouts/Layout';
import CompanyInfo from '../../components/admin/customers/Profile/CompanyInfo';
import SenderInfo from '../../components/admin/customers/Profile/SenderInfo';
import Agreements from '../../components/admin/customers/Profile/Agreements';
import ContactInfo from '../../components/admin/customers/Profile/ContactInfo';
import Campaign from '../../components/admin/customers/Profile/Campaign';
import InternalTCR from '../../components/admin/customers/Profile/InternalTCR';
import { AdminRoutes } from '../../routes/AdminRoutes';
import FormattedMessages from '../../messages/admin/CustomerProfileMessages';

import useCampaignRequest from '../../requests/CampaignRequest';
import useCustomerRequest from '../../requests/CustomerRequest';
import useAdminRequest from '../../requests/AdminRequest';
import Loader from '../../components/common/Loader';

const CustomerProfile = ({ accessLevel, accessType, errorHandler }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const enumsApplied = useRef(false);
  const { getOptions } = useCampaignRequest();
  const { getCompliance, updateCompliance } = useCustomerRequest();
  const {
    getSingleCustomer,
    deleteCustomerAttachment,
    addCustomerAttachments,
    downloadCustomerAttachment,
  } = useAdminRequest();

  const [highlighted, setHighlighted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('company');
  const [profile, setProfile] = useState({});
  const [enums, setEnums] = useState({});
  const [missing, setMissing] = useState({});
  const [labels, setLabels] = useState({
    company: FormattedMessages.company,
    sender: FormattedMessages.sender,
    agreements: FormattedMessages.agreements,
    contact: FormattedMessages.contact,
    campaign: FormattedMessages.campaign,
    internal: FormattedMessages.internal,
  });

  /**
   * First load for all the data
   */
  useEffect(() => {
    const loadEnums = async () => {
      setEnums(
        await getOptions(
          [
            'brandRelationship',
            'entityType',
            'stockExchange',
            'usecase',
            'vertical',
            'cnp',
          ],
          errorHandler
        )
      );
    };

    loadCustomer();
    loadProfile();
    loadEnums();
  }, []);

  useEffect(() => {
    applyEnums();
    calcMissing();
  }, [profile, enums]);

  /**
   * When we know how many still need to be filled in, we can update the tab labels accordingly
   */
  useEffect(() => {
    const labels = {
      company: FormattedMessages.company,
      sender: FormattedMessages.sender,
      agreements: FormattedMessages.agreements,
      contact: FormattedMessages.contact,
      campaign: FormattedMessages.campaign,
      internal: FormattedMessages.internal,
    };

    Object.entries(missing).forEach(([section, count]) => {
      if (count === 0) return;

      labels[section] = (
        <>
          {labels[section]} ({count})
        </>
      );
    });

    setLabels(labels);
  }, [missing]);

  const loadCustomer = async () => {
    const customer = await getSingleCustomer(id, errorHandler);

    console.log('customer', customer);
    setCustomer(customer);
  };

  const loadProfile = async () => {
    setLoading(true);
    const compliance = await getCompliance(id, errorHandler);

    console.log(compliance);

    const profile = {
      iso_code_2: compliance.iso_code_2,
    };
    Object.entries(rules).forEach(([section, fields]) => {
      profile[section] = {};

      Object.keys(fields).forEach((field) => {
        let value = compliance[field];

        if (value && ['sub_use_cases', 'send_to_countries'].includes(field)) {
          value = value.split(',');
        }

        if (
          value &&
          [
            'trademark_certificate',
            'proof_of_authority',
            'message_flow_files',
          ].includes(field)
        ) {
          if (compliance.attachments) {
            value = value
              .split(',')
              .map((v) => {
                const id = parseInt(v.trim());
                const attachment = compliance.attachments.find(
                  (a) => a.customer_attachment_id === id
                );

                if (!attachment) return null;

                return {
                  ...attachment,
                  id: attachment.customer_attachment_id,
                };
              })
              .filter((v) => !!v);
          } else {
            value = [];
          }
        }

        profile[section][field] = {
          value,
          required: false,
          visible: false,
        };
      });
    });

    console.log('loadProfile', profile);
    setProfile(profile);
    setLoading(false);
  };

  const applyEnums = () => {
    if (enumsApplied.current) return;
    if (!enums?.usecase) return;
    if (!profile?.campaign) return;

    const p = { ...profile };

    const useCase = enums.usecase[p.campaign.use_case.value];

    if (!useCase) return;

    p.campaign.use_case.obj = useCase;
    setProfile(p);
    enumsApplied.current = true;
  };

  const calcMissing = () => {
    if (!Object.keys(profile).length) return;
    if (!Object.keys(enums).length) return;

    const missing = {};

    Object.entries(rules).forEach(([section, fields]) => {
      missing[section] = 0;

      Object.entries(fields).forEach(([field, rule]) => {
        const pField = profile[section][field];
        pField.required = rule.required(profile);
        pField.visible = rule.visible(profile);

        if (!pField.required) return;
        if (!pField.visible) return;

        if (typeof pField.value === 'boolean') {
          if (pField.required === 'set') return; // can be false (Yes/No)
          if (pField.value) return;
        }
        if (Array.isArray(pField.value) && pField.value.length) return;
        if (typeof pField.value === 'number') return;
        if (typeof pField.value === 'string' && pField.value.length) return;
        if (typeof pField.value === 'object') return;

        console.log('missing', section, field);
        missing[section]++;
      });
    });

    console.log('missing', missing);
    setMissing(missing);
  };

  const handleTabChange = (_event, newValue) => {
    setTab(newValue);
  };

  const handleChange = (section, value) => {
    console.log('handleChange', section, value);

    const p = { ...profile };
    p[section] = value;

    if (section === 'company' && value.country_registered.country) {
      p.iso_code_2 = value.country_registered.country?.iso_code_2;
    }
    setProfile(p);
  };

  const handleDownload = async (file) => {
    await downloadCustomerAttachment(
      id,
      file.customer_attachment_id,
      file.attachment_name,
      errorHandler
    );
  };

  const uploadFiles = async (files) => {
    if (!files) return [];

    if (Array.isArray(files)) {
      const promises = [];

      files.forEach((file) => promises.push(uploadFiles(file)));

      const values = await Promise.all(promises);

      return values.filter((v) => !!v);
    }

    if (files.customer_attachment_id) {
      if (files.deleted) {
        await deleteCustomerAttachment(
          id,
          files.customer_attachment_id,
          errorHandler
        );

        return;
      }

      return files.customer_attachment_id;
    }

    if (files.deleted) return;
    if (!files.attachment_file) return;

    const attachment = await addCustomerAttachments(
      id,
      'COMPANY',
      files.attachment_file,
      errorHandler,
      'compliance'
    );

    return attachment.customer_attachment_id;
  };

  const handleSubmit = async () => {
    let valid = true;

    Object.entries(profile).forEach(([section, fields]) => {
      if (!fields) return;

      Object.entries(fields).forEach(([name, field]) => {
        if (field?.error) field.error = null;

        if (
          !field.visible ||
          typeof field.value === 'undefined' ||
          field.value === '' ||
          !field.value.length
        )
          return;

        const rule = rules[section][name];

        if (rule.error) field.error = rule.error(field.value);

        if (field.error) console.log('error', name, field.error);

        if (field.error) valid = false;
      });
    });

    if (!valid) {
      setProfile({ ...profile });
      return;
    }

    setSaving(true);
    const compliance = {};

    Object.entries(profile).forEach(([section, fields]) => {
      if (section === 'iso_code_2') return;

      Object.entries(fields).forEach(([name, field]) => {
        if (typeof field.value === 'undefined') return;

        switch (name) {
          case 'trademark_certificate':
          case 'proof_of_authority':
          case 'message_flow_files':
            break;
          case 'send_to_countries':
            if (Array.isArray(field.value)) {
              compliance.send_to_countries = field.value
                .map((country) => {
                  console.log('send_to_countries', country);
                  if (typeof country === 'object')
                    return country.country_code_id;
                  return country;
                })
                .filter((v) => !!v)
                .join(',');
            } else {
              compliance.send_to_countries = '';
            }
            break;
          case 'sub_use_cases':
            compliance[name] = field.value.join(',');
            break;
          case 'dca':
            compliance[name] =
              typeof field.value === 'object' ? field.value.id : field.value;
            break;
          default:
            compliance[name] = field.value;
        }
      });
    });

    console.log('saving compliance', compliance);

    const filePromises = [];

    if (profile.company.trademark_certificate.value) {
      filePromises.push(
        await (async () => {
          const values = await uploadFiles(
            profile.company.trademark_certificate.value
          );

          compliance.trademark_certificate = values.join(',');
        })()
      );
    }
    if (profile.company.proof_of_authority.value) {
      filePromises.push(
        await (async () => {
          const values = await uploadFiles(
            profile.company.proof_of_authority.value
          );

          compliance.proof_of_authority = values.join(',');
        })()
      );
    }
    if (profile.campaign.message_flow_files.value) {
      filePromises.push(
        await (async () => {
          const values = await uploadFiles(
            profile.campaign.message_flow_files.value
          );

          compliance.message_flow_files = values.join(',');
        })()
      );
    }

    await Promise.all(filePromises);
    await updateCompliance(id, compliance, errorHandler);
    await loadProfile();
    setSaving(false);
  };

  return (
    <Layout
      routes={AdminRoutes}
      title={
        <>
          {customer?.customer_name || 'Loading'} -{' '}
          {FormattedMessages.companyProfile}
        </>
      }
      backButtonText={FormattedMessages.backToCustomer}
      handleBackButton={() => navigate(-1)}
      accessLevel={accessLevel}
      accessType={accessType}
      titleRowRightComponent={
        <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
          <FormControlLabel
            control={
              <Switch
                checked={highlighted}
                onChange={(e) => setHighlighted(e.target.checked)}
              />
            }
            label="Highlight Required"
          />
          <Box sx={{ xs: { mb: 4 }, md: { mb: 0, marginY: 4 } }}>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? FormattedMessages.saving : FormattedMessages.save}
            </Button>
          </Box>
        </Stack>
      }
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          <Box sx={{ borderBottom: 2, borderColor: 'divider' }}>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              aria-label="company profile tabs"
            >
              <Tab label={labels.company} value="company" />
              <Tab label={labels.sender} value="sender" />
              <Tab label={labels.agreements} value="agreements" />
              <Tab label={labels.contact} value="contact" />
              <Tab
                label={labels.campaign}
                value="campaign"
                sx={{
                  color: Object.values(profile?.campaign || {}).some(
                    (v) => v.error
                  )
                    ? 'error.main'
                    : 'secondary',
                  '&.Mui-selected': {
                    color: Object.values(profile?.campaign || {}).some(
                      (v) => v.error
                    )
                      ? 'error.main'
                      : 'secondary',
                  },
                }}
              />
              <Tab label={labels.internal} value="internal" />
            </Tabs>
          </Box>

          <Box sx={{ padding: 2 }}>
            {tab === 'company' && (
              <CompanyInfo
                profile={profile}
                enums={enums}
                onChange={handleChange}
                onDownload={handleDownload}
                highlighted={highlighted}
              />
            )}
            {tab === 'sender' && (
              <SenderInfo
                profile={profile}
                onChange={handleChange}
                highlighted={highlighted}
              />
            )}
            {tab === 'agreements' && (
              <Agreements
                profile={profile}
                onChange={handleChange}
                highlighted={highlighted}
              />
            )}
            {tab === 'contact' && (
              <ContactInfo
                profile={profile}
                onChange={handleChange}
                highlighted={highlighted}
              />
            )}
            {tab === 'campaign' && (
              <Campaign
                profile={profile}
                enums={enums}
                onChange={handleChange}
                onDownload={handleDownload}
                highlighted={highlighted}
              />
            )}
            {tab === 'internal' && (
              <InternalTCR
                profile={profile}
                enums={enums}
                onChange={handleChange}
                highlighted={highlighted}
              />
            )}
          </Box>

          <Dialog open={saving}>
            <DialogTitle>{FormattedMessages.saving}</DialogTitle>
            <DialogContent>
              <Stack direction="row">
                {FormattedMessages.savingMessage}
                <CircularProgress size="20px" />
              </Stack>
            </DialogContent>
          </Dialog>
        </>
      )}
    </Layout>
  );
};

export default CustomerProfile;

const rules = {
  company: {
    country_registered: {
      required: () => true,
      visible: () => true,
    },
    ip_address: {
      required: () => true,
      visible: () => true,
    },
    company_legal_name: {
      required: () => true,
      visible: () => true,
    },
    account_name: {
      required: () => true,
      visible: () => true,
    },
    registration_id: {
      required: () => true,
      visible: () => true,
    },
    proof_of_authority: {
      required: () => true,
      visible: (profile) => ['AU', 'UK'].includes(profile.iso_code_2),
    },
    business_vertical: {
      required: () => true,
      visible: () => true,
    },
    business_product: {
      required: () => true,
      visible: () => true,
    },
    trademark_certificate: {
      required: () => false,
      visible: (profile) => ['AU'].includes(profile.iso_code_2),
    },
    address_1: {
      required: () => true,
      visible: () => true,
    },
    address_2: {
      required: () => false,
      visible: () => true,
    },
    city: {
      required: () => true,
      visible: () => true,
    },
    state: {
      required: () => true,
      visible: () => true,
    },
    postal_code: {
      required: () => true,
      visible: () => true,
    },
    country: {
      required: () => true,
      visible: () => true,
    },
    business_phone: {
      required: () => true,
      visible: () => true,
    },
    website: {
      required: () => true,
      visible: () => true,
    },
    identifier_type: {
      required: () => false,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    identifier_number: {
      required: () => false,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    entity_type: {
      required: () => true,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    stock_exchange: {
      required: (profile) => profile.company.entity_type === 'PUBLIC_PROFIT',
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    stock_exchange_symbol: {
      required: (profile) => !!profile.company.stock_exchange.value,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    brand_relationship: {
      required: () => true,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    billing_address_same: {
      required: () => false,
      visible: () => true,
    },
    billing_address_1: {
      required: (profile) => !profile.company.billing_address_same.value,
      visible: () => true,
    },
    billing_address_2: {
      required: () => false,
      visible: () => true,
    },
    billing_city: {
      required: (profile) => !profile.company.billing_address_same.value,
      visible: () => true,
    },
    billing_state: {
      required: (profile) => !profile.company.billing_address_same.value,
      visible: () => true,
    },
    billing_postal_code: {
      required: (profile) => !profile.company.billing_address_same.value,
      visible: () => true,
    },
    billing_country: {
      required: (profile) => !profile.company.billing_address_same.value,
      visible: () => true,
    },
    business_classification: {
      required: () => true,
      visible: () => true,
    },
    use_ro_numbers: {
      required: (profile) =>
        profile.company.business_classification.value === 'isv' ? 'set' : false,
      visible: () => true,
    },
    send_international: {
      required: () => 'set',
      visible: () => true,
    },
    send_to_countries: {
      required: (profile) => profile.company.send_international.value,
      visible: () => true,
    },
  },
  sender: {
    uses_sender_id: {
      required: () => false,
      visible: (profile) => ['AU', 'UK'].includes(profile.iso_code_2),
    },
    sender_id_value: {
      required: (profile) => profile.sender.uses_sender_id.value,
      visible: (profile) => ['AU', 'UK'].includes(profile.iso_code_2),
    },
    sender_id_relationship: {
      required: (profile) => profile.sender.uses_sender_id.value,
      visible: (profile) => ['AU', 'UK'].includes(profile.iso_code_2),
    },
    sender_id_sample: {
      required: (profile) => profile.sender.uses_sender_id.value,
      visible: (profile) => ['AU', 'UK'].includes(profile.iso_code_2),
    },
    sender_comments: {
      required: () => false,
      visible: (profile) => ['AU', 'UK'].includes(profile.iso_code_2),
    },
  },
  agreements: {
    read_compliance_agreements: {
      required: () => true,
      visible: () => true,
    },
    filled_truthfully: {
      required: () => true,
      visible: () => true,
    },
    service_agreement_agreed: {
      required: () => true,
      visible: () => true,
    },
    service_agreement_date: {
      required: () => true,
      visible: () => true,
    },
  },
  contact: {
    authorized_first_name: {
      required: () => true,
      visible: () => true,
    },
    authorized_last_name: {
      required: () => true,
      visible: () => true,
    },
    authorized_position: {
      required: () => true,
      visible: () => true,
    },
    authorized_work_phone: {
      required: () => true,
      visible: () => true,
    },
    authorized_mobile: {
      required: () => true,
      visible: () => true,
    },
    authorized_contact_email: {
      required: () => true,
      visible: () => true,
    },

    owner_first_name: {
      required: () => true,
      visible: () => true,
    },
    owner_last_name: {
      required: () => true,
      visible: () => true,
    },
    owner_position: {
      required: () => true,
      visible: () => true,
    },
    owner_work_phone: {
      required: () => true,
      visible: () => true,
    },
    owner_contact_email: {
      required: () => true,
      visible: () => true,
    },

    billing_first_name: {
      required: () => true,
      visible: () => true,
    },
    billing_last_name: {
      required: () => true,
      visible: () => true,
    },
    billing_position: {
      required: () => true,
      visible: () => true,
    },
    billing_work_phone: {
      required: () => true,
      visible: () => true,
    },
    billing_contact_email: {
      required: () => true,
      visible: () => true,
    },

    technical_first_name: {
      required: () => true,
      visible: () => true,
    },
    technical_last_name: {
      required: () => true,
      visible: () => true,
    },
    technical_position: {
      required: () => true,
      visible: () => true,
    },
    technical_work_phone: {
      required: () => true,
      visible: () => true,
    },
    technical_mobile: {
      required: () => true,
      visible: () => true,
    },
    technical_contact_email: {
      required: () => true,
      visible: () => true,
    },
  },
  campaign: {
    use_case: {
      required: () => true,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    sub_use_cases: {
      required: (profile) => {
        if (typeof profile?.campaign?.use_case?.obj !== 'object') return false;
        return profile.campaign.use_case.obj.minSubUsecases > 0;
      },
      visible: (profile) => {
        if (!['US'].includes(profile.iso_code_2)) return false;
        if (typeof profile?.campaign?.use_case?.obj !== 'object') return false;
        return profile.campaign.use_case.obj.maxSubUsecases > 0;
      },
    },
    message_description: {
      required: () => true,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
      error: (value) => {
        if (!value?.length) return false;
        if (value.length >= 40) return false;
        return 'Min Length: 40 characters';
      },
    },
    sample_1: {
      required: () => true,
      visible: () => true,
      error: (value) => {
        if (!value?.length) return false;
        if (value.length >= 20) return false;
        return 'Min Length: 20 characters';
      },
    },
    sample_2: {
      required: () => false,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    sample_3: {
      required: () => false,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    sample_4: {
      required: () => false,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    sample_5: {
      required: () => false,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    subscriber_opt_in: {
      required: () => 'set',
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    opt_in_keywords: {
      required: (profile) => profile.campaign.subscriber_opt_in.value,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    opt_in_message: {
      required: (profile) => profile.campaign.subscriber_opt_in.value,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    subscriber_opt_out: {
      required: () => 'set',
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    opt_out_keywords: {
      required: (profile) => profile.campaign.subscriber_opt_out.value,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    opt_out_message: {
      required: (profile) => profile.campaign.subscriber_opt_out.value,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    subscriber_help: {
      required: () => 'set',
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    help_keywords: {
      required: (profile) => profile.campaign.subscriber_help.value,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    help_message: {
      required: (profile) => profile.campaign.subscriber_help.value,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    embedded_link: {
      required: () => 'set',
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    embedded_link_sample: {
      required: (profile) => profile.campaign.embedded_link.value,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    embedded_phone: {
      required: () => 'set',
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    embedded_phone_sample: {
      required: (profile) => profile.campaign.embedded_phone.value,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    number_pool: {
      required: () => 'set',
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    loan_agreements: {
      required: () => 'set',
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    affiliate_marketing: {
      required: () => 'set',
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    age_gated: {
      required: () => 'set',
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    service_agreement_url: {
      required: () => true,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    privacy_policy_url: {
      required: () => true,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    message_flow: {
      required: () => true,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    message_flow_files: {
      required: () => false,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
  },
  internal: {
    csp: {
      required: () => true,
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
    dca: {
      required: () => true,
      visible: () => true,
    },
    auto_renewal: {
      required: () => 'set',
      visible: (profile) => ['US'].includes(profile.iso_code_2),
    },
  },
};
